import { useEffect, useMemo, useState } from 'react';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { useMutation, useQuery } from 'services/api/hooks';
import {
  deleteCampaign,
  getCampaignSubscriptionsByUserAndOrganization,
  updateCampaign,
  launchCampaign as launchCampaignApi
} from 'services/campaign/api';
import { LaunchCampaignModal } from './subpages/components/LaunchCampaignModal';
import { ScheduleLaunchModal } from './subpages/components/ScheduleLaunchModal';
import { useRecoilValue } from 'recoil';
import { userAtom } from 'services/user/atoms';
import {
  Campaign,
  CampaignStatus,
  CampaignSubscriptionWithCampaign
} from 'services/campaign/types';
import {
  CampaignStatus as CampaignStatusGenerated,
  SearchableConversationFilterInput
} from 'generated-graphql/types';
import { CampaignStatusChip } from 'components/CampaignStatus';
import { TableV2, TableV2Column } from 'components/Table/TableV2';
import { formatDateFromNowAbbreviated, sortByDate } from 'utils/datetime';
import { useConfirmationModal, useInputModal } from 'services/modals/hooks';
import { ConfirmationModalType } from 'services/modals/constants';
import {
  textChannelByChannelValue,
  STOPPABLE_CAMPAIGN_STATUSES
} from 'services/campaign/constants';
import { statusFilterOptions } from './constants';
import { useCampaignIdsWithMoreTimeRequested } from 'modules/campaigns/hooks/useCampaignIdsWithMoreTimeRequested';
import { useCampaignsList } from 'modules/campaigns/hooks/useCampaignsList';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { ToastType, useToast } from 'services/toast';
import { ActionOption, ActionPopover } from 'components/ActionPopover';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { StopCampaignDrawer } from 'modules/campaigns/components/StopCampaignDrawer';
import { mapToDuplicateCampaignInput } from 'modules/campaigns/utils/mapToDuplicateCampaignInput';
import { integrationAtom } from 'services/integration/atom';
import { getAtsJobUrl } from 'services/integration/utils/getAtsJobUrl';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { handleFilterTracking } from './utils/handleFilterTracking';
import { organizationAtom } from 'services/organization/atom';
import { useIsAdmin } from 'hooks/useIsAdmin';
import {
  useCreateDuplicateCampaignMutation,
  useGetCampaignForDuplicationLazyQuery
} from 'generated-graphql/hooks/apollo-hooks';
import {
  mapOrganizationCampaignsToCampaigns,
  mapUserCampaignsToCampaigns
} from 'utils/generatedCampaignMappers';

import { getDefaultCampaignsQueryVariables } from 'modules/campaigns/utils/getDefaultCampaignsQueryVariables';
import { useAllSearchCampaigns } from 'modules/campaigns/hooks/useAllSearchCampaigns';
import { useCampaignsGroupHandler } from './hooks/useCampaignsGroupHandler';
import { useUserOrganizationOptions } from './hooks/useUserOrganizationOptions';
import { CampaignJobTitleCell } from './components/CampaignJobTitleCell';
import { labelByValue, TabsValue } from 'modules/campaigns/constants/tabs';
import { CampaignListItem } from 'modules/campaigns/types/CampaignListItem';
import { CampaignReviewIcon } from 'modules/campaigns/components/CampaignReviewIcon';
import { resolveCampaignsTab } from 'modules/campaigns/utils/resolveCampaignsTab';
import { CrossCampaignConversationsTab } from 'modules/conversations/components/CrossCampaignConversationsTab';
import { useCrossCampaignConversationsCount } from 'modules/conversations/hooks';
import { reviewFilterOptions } from 'modules/campaigns/constants/reviewFilterOptions';
import { getCampaignFilterLabel } from 'modules/campaigns/utils/getCampaignFilterLabel';
import { CampaignFilterIds } from './constants/filters';
import { useInitialCampaignFilters } from 'modules/campaigns/hooks/useInitialCampaignFilters';
import { useExportCampaignsData } from 'modules/campaigns/hooks/useExportCampaignsData';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';
import {
  externalIdFields,
  externalIdSchema,
  externalIdSchemaOptional
} from 'common/form/validationSchema/externalId';
import { GlobalContextButton } from 'modules/organization/components/GlobalContextButton';
import { StartCampaignFeatureFlag } from './components/StartCampaignFeatureFlag';
import { TabsV2 } from 'common/ui/TabsV2';
import {
  layoutContainerPaddingBottom,
  layoutContainerPaddingTop,
  layoutContainerPaddingX
} from 'services/navigation/components/PrivateRoute';
import { ConversationSettingsDrawer } from 'modules/organization/components/ConversationSettingsDrawer';

const container: SxProps = {
  pt: layoutContainerPaddingTop,
  pb: layoutContainerPaddingBottom,
  px: layoutContainerPaddingX
};

const checkBoxesFilterContainer: SxProps = {
  display: 'flex',
  gap: 2,
  my: 2
};

const pageTitle: SxProps<Theme> = {
  fontWeight: 'bold',
  color: 'customGrey.dark',
  lineHeight: '60px',
  letterSpacing: '-0.96px',
  mt: '10px',
  mb: '40px'
};

// TabsV2 keeps its 6px border elsewhere. The selectors carry one more class
// than the component's own rules to win.
const tabBar: SxProps<Theme> = {
  '& .MuiTabs-root .MuiTabs-indicator': {
    height: '5px'
  },
  '& .MuiTabs-root .MuiTab-root': {
    borderTopWidth: '5px'
  }
};

// The design's 52px New campaign button. The shared Button is 56px.
const newCampaignButton: SxProps<Theme> = {
  padding: '14px 20px'
};

const CampaignAction = ({
  campaign,
  removeCampaignFromList,
  updateCampaignInList,
  onDuplicateClick,
  onLaunchSuccess
}: {
  campaign: CampaignListItem;
  removeCampaignFromList: (campaignId: string) => void;
  updateCampaignInList: (campaign: Campaign) => void;
  onDuplicateClick: (campaignId: string) => void;
  onLaunchSuccess: () => void;
}) => {
  const integrationCredentials = useRecoilValue(integrationAtom);
  const isExternalIdRequiredFlag = useFlag(Flags.EXTERNAL_ID_IS_MANDATORY);
  const isEditExternalIdEnabledFlag = useFlag(Flags.EDIT_EXTERNAL_ID);

  const { showModal } = useConfirmationModal();
  const { showModal: showInputModal } = useInputModal();
  const { openModal } = useModalV2();
  const { showToast } = useToast();
  const { track } = useTracking();
  const { openDrawer } = useDrawerV2();

  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false);
  const [isLaunchNowModalOpen, setIsLaunchNowModalOpen] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);

  const { id: campaignId, status, atsJobId } = campaign;
  const isReadyForLaunch =
    status === CampaignStatus.FINISHED_GENERATING &&
    !!campaign.totalProspectsCount;
  const isScheduled = status === CampaignStatus.SCHEDULED;

  const { mutate: deleteCampaignAction } = useMutation(deleteCampaign, {
    errorTextMessage: 'Sorry we could not delete this campaign',
    successTextMessage: 'Campaign deleted successfully'
  });

  const { mutate: updateCampaignAction } = useMutation(updateCampaign, {
    errorTextMessage: "Sorry we could not update this campaign's status",
    disableSuccessToast: true
  });

  const isDraft = status === CampaignStatus.DRAFT;
  const isArchive = status === CampaignStatus.ARCHIVED;
  const isChild = campaign.campaignGroupId && !campaign.isParent;
  const isStoppable = (STOPPABLE_CAMPAIGN_STATUSES as string[]).includes(
    status
  );

  const onDeleteClick = () => {
    showModal({
      title: 'Are you sure?',
      message:
        'Deleting it will permanently remove all associated content, and this action cannot be undone.',
      onDestructiveConfirm: async () => {
        await deleteCampaignAction({ id: campaignId });
        removeCampaignFromList(campaignId);
      },
      modalType: ConfirmationModalType.DESTRUCTIVE,
      cancelText: 'Keep campaign',
      destructiveText: 'Delete campaign'
    });
  };

  const onArchiveClick = () => {
    showModal({
      title: 'Are you sure?',
      message:
        'Once a campaign is archived, active conversations will stop. Archived campaigns cannot be re-activated in the future.',
      onDestructiveConfirm: async () => {
        track(TrackingEventName.CampaignsListActionArchive);
        await updateCampaignAction({
          id: campaignId,
          status: CampaignStatus.ARCHIVED
        });
        removeCampaignFromList(campaignId);
      },
      modalType: ConfirmationModalType.DESTRUCTIVE,
      cancelText: 'Keep campaign',
      destructiveText: 'Archive campaign'
    });
  };

  const onStopClick = () => {
    openDrawer({
      title: 'Stop campaign',
      width: '450px',
      children: (
        <StopCampaignDrawer
          campaign={campaign as Campaign}
          onCompleted={() => removeCampaignFromList(campaignId)}
        />
      )
    });
  };

  const onRenameClick = () => {
    showInputModal({
      title: 'Rename campaign',
      inputLabel: 'Campaign title',
      initialValue: String(campaign.title),
      onConfirmClick: async (value) => {
        const campaign = await updateCampaignAction({
          id: campaignId,
          title: value
        });
        showToast({
          title: 'Campaign name updated',
          type: ToastType.Success
        });
        updateCampaignInList(campaign);
      }
    });
  };

  const onEditExternalIdClick = () => {
    openModal({
      title: 'Edit external ID',
      form: {
        schema: isExternalIdRequiredFlag
          ? externalIdSchema
          : externalIdSchemaOptional,
        fields: externalIdFields,
        defaultValues: {
          externalId: campaign.externalId ?? ''
        }
      },
      onPrimaryAction: async (values) => {
        const campaign = await updateCampaignAction({
          id: campaignId,
          externalId: values.externalId || null
        });
        showToast({
          title: 'External ID updated',
          type: ToastType.Success
        });
        updateCampaignInList(campaign);
      }
    });
  };

  const handleLaunchCampaign = async (
    launchScheduleDateTime: string | undefined
  ): Promise<boolean> => {
    try {
      await launchCampaignApi({
        campaignId,
        launchScheduleDateTime
      });
      showToast({
        message: launchScheduleDateTime
          ? 'Campaign scheduled successfully'
          : 'Campaign launched successfully',
        type: ToastType.Success
      });
      onLaunchSuccess();
      return true;
    } catch {
      showToast({
        message: 'Failed to launch campaign. Please try again.',
        type: ToastType.Error
      });
      return false;
    }
  };

  const onViewAtsJobClick = () => {
    const url = getAtsJobUrl({ integrationCredentials, atsJobId });
    if (!url) {
      showToast({
        message: 'Failed to open job with your ats',
        type: ToastType.Error
      });
      return;
    }
    window.open(url, '_blank');
  };

  const rowActionsOptions = useMemo<ActionOption[]>(() => {
    const options: ActionOption[] = [];

    if (isReadyForLaunch) {
      options.push({
        label: 'Launch',
        onClick: () => {
          track(TrackingEventName.CampaignsListActionLaunch);
          setIsLaunchModalOpen(true);
        }
      });
    }

    if (isScheduled) {
      options.push({
        label: 'Launch now',
        onClick: () => {
          track(TrackingEventName.CampaignsListActionLaunch);
          setIsLaunchNowModalOpen(true);
        }
      });
      options.push({
        label: 'Reschedule launch',
        onClick: () => {
          setIsRescheduleModalOpen(true);
        }
      });
    }

    if (atsJobId) {
      options.push({
        label: 'View job in ATS',
        onClick: onViewAtsJobClick
      });
    }

    if (!isChild && !campaign.workflowId) {
      options.push({
        label: 'Duplicate',
        onClick: () => {
          track(TrackingEventName.CampaignsListActionDuplicate);
          onDuplicateClick(campaignId);
        }
      });
    }

    if (!isArchive) {
      options.push({
        label: 'Archive',
        onClick: onArchiveClick
      });
    }

    options.push({
      label: 'Rename',
      onClick: onRenameClick
    });

    if (isEditExternalIdEnabledFlag) {
      options.push({
        label: 'Edit ID',
        onClick: onEditExternalIdClick
      });
    }

    if (isDraft) {
      options.push({
        label: 'Delete',
        onClick: onDeleteClick,
        itemColor: 'error.main'
      });
    }

    if (isStoppable) {
      options.push({
        label: 'Stop campaign',
        onClick: onStopClick,
        itemColor: 'error.main'
      });
    }

    return options;
  }, [
    campaign.title,
    isDraft,
    isArchive,
    isStoppable,
    campaign.externalId,
    isReadyForLaunch,
    isScheduled
  ]);

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <ActionPopover id={campaignId} options={rowActionsOptions} />
      </Box>

      <LaunchCampaignModal
        isOpen={isLaunchModalOpen}
        onClose={() => setIsLaunchModalOpen(false)}
        onLaunchCampaign={handleLaunchCampaign}
      />

      {/* Launch now confirmation modal (for scheduled campaigns) */}
      <LaunchCampaignModal
        isOpen={isLaunchNowModalOpen}
        onClose={() => setIsLaunchNowModalOpen(false)}
        onLaunchCampaign={handleLaunchCampaign}
        confirmOnly
      />

      {/* Reschedule modal (for scheduled campaigns) */}
      <ScheduleLaunchModal
        isOpen={isRescheduleModalOpen}
        onClose={() => setIsRescheduleModalOpen(false)}
        onConfirm={(scheduledDateTime) =>
          handleLaunchCampaign(scheduledDateTime)
        }
      />
    </>
  );
};

export const Campaigns = () => {
  const isCampaignCreationV3Enabled = useFlag(Flags.CAMPAIGN_CREATION_V3);
  const navigate = useNavigate();
  const user = useRecoilValue(userAtom);
  const organization = useRecoilValue(organizationAtom);
  const { openDrawer: openDrawerV2 } = useDrawerV2();

  const [campaignsList, setCampaignsList] = useState<Campaign[]>([]);
  const [campaignSubscriptions, setCampaignSubscriptions] = useState<
    CampaignSubscriptionWithCampaign[]
  >([]);

  const {
    userOrganizationOptions,
    isLoading: userOrganizationOptionsIsLoading
  } = useUserOrganizationOptions();

  const initialFilters = useInitialCampaignFilters({ userOrganizationOptions });
  const [filters, setFilters] = useState<
    {
      filterId: string;
      value: string | boolean;
      label: string;
    }[]
  >(initialFilters);
  const { showToast } = useToast();
  const { track } = useTracking();
  const [sortKey, setSortKey] = useState('createdAt');

  const { isAdmin } = useIsAdmin();

  const [isChecked, setIsChecked] = useState(false);

  const isConversationsTabEnabled = useFlag(
    Flags.CROSS_CAMPAIGN_CONVERSATIONS_TAB
  );

  // Undefined until the user picks a tab, so the default can follow the flag
  // without an effect writing state after the fact.
  const [selectedTab, setSelectedTab] = useState<TabsValue>();
  const [searchParams] = useSearchParams();
  // Read once: closing the drawer removes conversationId from the URL, and
  // that must not move the user off the tab the link opened.
  const [openedFromConversationLink] = useState(() =>
    searchParams.has('conversationId')
  );
  const tab = resolveCampaignsTab({
    selectedTab,
    isConversationsTabEnabled,
    hasConversationLink: openedFromConversationLink
  });

  const [createDuplicateCampaign] = useCreateDuplicateCampaignMutation();
  const [getCampaignForDuplication] = useGetCampaignForDuplicationLazyQuery();

  // Reported by the Conversations tab while a search or filter is applied.
  const [conversationsExportFilter, setConversationsExportFilter] =
    useState<SearchableConversationFilterInput>();

  // Only the Conversations tab narrows the export. The Campaigns tab exports
  // the whole account, as it always has.
  const exportFilter =
    tab === TabsValue.CONVERSATIONS ? conversationsExportFilter : undefined;
  const { exportData, isExporting, hasExported } = useExportCampaignsData({
    filter: exportFilter
  });

  const getExportButtonLabel = () => {
    if (isExporting) return 'Exporting';
    if (hasExported) return 'Export sent';
    return exportFilter ? 'Export data' : 'Export all data';
  };

  const {
    loading: organizationCampaignsIsLoading,
    error: organizationCampaignsError,
    refetch: organizationCampaignsRefetch
  } = useAllSearchCampaigns({
    variables: getDefaultCampaignsQueryVariables({
      organizationId: organization?.id
    }),
    skip: !organization?.id || !isAdmin,
    onCompleted: (result) => {
      const mappedCampaigns = mapOrganizationCampaignsToCampaigns(
        result.searchCampaigns
      );
      setCampaignsList(mappedCampaigns);
    }
  });

  const refetchAndSetOrganizationCampaigns = async () => {
    try {
      const refetchedData = await organizationCampaignsRefetch();
      const mappedCampaigns = mapOrganizationCampaignsToCampaigns(
        refetchedData.data.searchCampaigns
      );
      setCampaignsList(mappedCampaigns);
    } catch {
      // Error is surfaced via the campaignsError toast below.
    }
  };

  const {
    loading: userCampaignIsLoading,
    error: userCampaignsError,
    refetch: userCampaignsRefetch
  } = useAllSearchCampaigns({
    variables: getDefaultCampaignsQueryVariables({
      organizationId: organization?.id,
      userId: user?.id
    }),
    skip: !user || !organization || isAdmin,
    onCompleted: (result) => {
      const mappedCampaigns = mapUserCampaignsToCampaigns(
        result.searchCampaigns
      );
      setCampaignsList(mappedCampaigns);
    }
  });

  const refetchAndSetUserCampaigns = async () => {
    try {
      const refetchedData = await userCampaignsRefetch();
      const mappedCampaigns = mapUserCampaignsToCampaigns(
        refetchedData.data.searchCampaigns
      );
      setCampaignsList(mappedCampaigns);
    } catch {
      // Error is surfaced via the campaignsError toast below.
    }
  };

  const campaignsError = organizationCampaignsError || userCampaignsError;

  useEffect(() => {
    if (campaignsError) {
      showToast({
        message: 'Something went wrong loading campaigns. Please try again.',
        type: ToastType.Error
      });
    }
  }, [campaignsError, showToast]);

  const removeCampaignFromList = (campaignId: string) => {
    setCampaignsList((prevCampaignsList) =>
      prevCampaignsList?.filter((campaign) => campaign.id !== campaignId)
    );
    setCampaignSubscriptions((prevCampSubs) =>
      prevCampSubs?.filter((sub) => sub.campaign.id !== campaignId)
    );
  };

  const updateCampaignInList = (updatedCampaign: Campaign) => {
    setCampaignsList((prevCampaignsList) =>
      prevCampaignsList?.map((campaign) =>
        campaign.id === updatedCampaign.id
          ? {
              ...campaign,
              ...updatedCampaign
            }
          : campaign
      )
    );
    setCampaignSubscriptions((prevCampSubs) =>
      prevCampSubs?.map((sub) =>
        sub.campaign.id === updatedCampaign.id
          ? {
              ...sub,
              campaign: {
                ...sub.campaign,
                ...updateCampaign
              }
            }
          : sub
      )
    );
  };

  const onDuplicateClick = async (campaignId: string) => {
    if (!user?.id || !organization?.id) {
      showToast({
        message: 'Something went wrong. Refresh and try again',
        type: ToastType.Error
      });
      return;
    }

    const { data } = await getCampaignForDuplication({
      variables: { id: campaignId }
    });

    const campaignToDuplicate = data?.getCampaign;

    if (!campaignToDuplicate) {
      showToast({
        message: 'Something went wrong. Refresh and try again',
        type: ToastType.Error
      });
      return;
    }

    const mappedCampaignInput = mapToDuplicateCampaignInput(
      user.id,
      organization.id,
      CampaignStatusGenerated.Draft,
      campaignToDuplicate
    );

    try {
      await createDuplicateCampaign({
        variables: {
          input: {
            ...mappedCampaignInput,
            isDuplicate: true,
            title: `${campaignToDuplicate.title} (copy)`
          }
        }
      });
      showToast({
        message: 'Campaign successfully duplicated',
        type: ToastType.Success
      });
    } catch (e) {
      showToast({
        message: 'Sorry we could not duplicate this campaign',
        type: ToastType.Error
      });
      return;
    }

    if (!isAdmin) {
      await campaignSubscriptionsRefetch();
      await refetchAndSetUserCampaigns();
    } else {
      await refetchAndSetOrganizationCampaigns();
    }
  };

  const navigateToCampaign = (campaign: CampaignListItem) => {
    if (campaign.status === CampaignStatus.DRAFT) {
      if (isCampaignCreationV3Enabled) {
        navigate(`${Paths.CAMPAIGN_CREATE_DRAFT}/${campaign.id}`);
        return;
      }

      navigate(`${Paths.CAMPAIGN_CREATE_DRAFT}/${campaign.id}`, {
        state: { campaign }
      });
      return;
    }

    navigate(`${Paths.OUTBOUND_CAMPAIGN}/${campaign.id}`);
  };

  const { campaignIds: campaignIdsWithMoreTimeRequested } =
    useCampaignIdsWithMoreTimeRequested(organization?.id);

  // TECH-DEBT: This is too convoluted to refactor
  const { list, campaignsCount } = useCampaignsList({
    tab,
    campaignsList: campaignsList || [],
    campaignSubscriptions: campaignSubscriptions || [],
    filters,
    isChecked: isAdmin ? isChecked : null,
    userId: user?.id,
    campaignIdsWithMoreTimeRequested
  });

  const {
    addChildrenCampaignsToParentGroupCampaign,
    removeChildrenCampaignsFromParentGroupCampaign
  } = useCampaignsGroupHandler({
    campaignsList,
    setCampaigns: setCampaignsList
  });

  const columns: TableV2Column<CampaignListItem>[] = useMemo(() => {
    return [
      {
        cell: (row) => <CampaignReviewIcon campaign={row} />,
        filterable: true,
        filterId: CampaignFilterIds.needsReview,
        filterOptions: reviewFilterOptions,
        center: true,
        width: '96px'
      },
      {
        name: 'Launched',
        selector: (row) =>
          row.status === CampaignStatus.FINISHED_SENDING ||
          row.status === CampaignStatus.LIVE
            ? formatDateFromNowAbbreviated(row.launchedAt ?? row.createdAt)
            : '-',
        sortable: true,
        id: 'launchedAt',
        maxWidth: '125px',
        sortFunction: (a, b) => {
          return sortByDate(
            a.launchedAt ?? a.createdAt,
            b.launchedAt ?? b.createdAt
          );
        }
      },
      {
        name: 'Job title',
        minWidth: '250px',
        cell: (row) => {
          return (
            <CampaignJobTitleCell
              campaign={row}
              onClick={navigateToCampaign}
              addChildrenCampaignsToParentGroupCampaign={
                addChildrenCampaignsToParentGroupCampaign
              }
              removeChildrenCampaignsFromParentGroupCampaign={
                removeChildrenCampaignsFromParentGroupCampaign
              }
              isFiltering={!!filters.length}
            />
          );
        },
        id: 'title',
        sortable: true,
        sortFunction: (a, b) => {
          const titleA = a.title || '';
          const titleB = b.title || '';
          return titleA.localeCompare(titleB);
        }
      },
      {
        name: 'Location',
        selector: (row) => {
          return row.jobDescriptionParsed?.location || 'N/A';
        },
        id: 'location',
        sortable: true,
        maxWidth: '120px'
      },
      {
        name: 'Sent to',
        selector: (row) => row?.totalConversationsCount || '',
        sortable: true,
        id: 'totalConversationsCount',
        width: '105px'
      },
      {
        name: 'Status',
        width: '200px',
        cell: (row) => (
          <CampaignStatusChip
            status={row.status}
            messagesProcessedCount={row.messagesProcessedCount}
            totalProspectsCount={row.totalProspectsCount}
          />
        ),
        filterable: true,
        filterId: 'status',
        filterOptions: statusFilterOptions.map((status) => ({
          value: status,
          label: getCampaignFilterLabel(CampaignFilterIds.status, status)
        }))
      },
      {
        name: 'Created by',
        selector: (row) => row.user?.name || '',
        id: 'createdBy',
        width: '170px',
        filterable: true,
        filterId: 'createdBy',
        filterOptions: userOrganizationOptions
      },
      {
        name: 'Channel',
        selector: (row) =>
          row.channel
            ? textChannelByChannelValue[
                row.channel as keyof typeof textChannelByChannelValue
              ]
            : '-',
        sortable: true,
        id: 'channel',
        width: '115px'
      },
      {
        name: 'External ID',
        selector: (row) => row.externalId || '',
        sortable: true,
        id: 'externalId',
        width: '150px'
      },
      {
        cell: (row) => (
          <CampaignAction
            campaign={row}
            removeCampaignFromList={removeCampaignFromList}
            updateCampaignInList={updateCampaignInList}
            onDuplicateClick={onDuplicateClick}
            onLaunchSuccess={() => {
              if (isAdmin) {
                refetchAndSetOrganizationCampaigns();
              } else {
                refetchAndSetUserCampaigns();
              }
            }}
          />
        ),
        isAction: true,
        width: '10px'
      }
    ];
  }, [
    tab,
    navigateToCampaign,
    addChildrenCampaignsToParentGroupCampaign,
    removeChildrenCampaignsFromParentGroupCampaign,
    filters.length
  ]);

  const campaignsIsLoading = useMemo(() => {
    return (
      organizationCampaignsIsLoading ||
      userCampaignIsLoading ||
      userOrganizationOptionsIsLoading
    );
  }, [
    organizationCampaignsIsLoading,
    userCampaignIsLoading,
    userOrganizationOptionsIsLoading
  ]);

  // Half of a non-admin's conversation visibility, so a failed lookup must not
  // settle as "no shared campaigns".
  const [hasCampaignSubscriptionsError, setHasCampaignSubscriptionsError] =
    useState(false);

  const {
    isLoading: campaignsSubscriptionsIsLoading,
    refetch: campaignSubscriptionsRefetch
  } = useQuery<{
    items: CampaignSubscriptionWithCampaign[];
  }>(
    getCampaignSubscriptionsByUserAndOrganization,
    {
      organizationId: organization?.id,
      userId: user?.id
    },
    {
      onFetchSuccess: (data) => {
        setHasCampaignSubscriptionsError(false);
        setCampaignSubscriptions(data?.items);
      },
      onError: () => {
        setHasCampaignSubscriptionsError(true);
        showToast({
          title: 'Could not load the campaigns shared with you',
          message: 'Please try again.',
          type: ToastType.Error
        });
      },
      preventInitialFetch: !user || isAdmin
    }
  );

  const isLoading =
    !user || campaignsIsLoading || campaignsSubscriptionsIsLoading;

  useEffect(() => {
    if (initialFilters.length) {
      setFilters(initialFilters);
    }
  }, [initialFilters]);

  const onFilterSelect = ({
    value,
    filterId
  }: {
    value: string | boolean;
    filterId?: string;
  }) => {
    if (filterId) {
      setFilters((prevFilters) => {
        return [
          ...prevFilters.filter((filter) => filter.filterId !== filterId),
          {
            filterId,
            value,
            label: getCampaignFilterLabel(filterId as CampaignFilterIds, value)
          }
        ];
      });

      handleFilterTracking({
        track,
        filterId
      });
    }
  };

  // Shared by the list and the tab badge, so the two cannot disagree about when
  // the restriction is usable. A failed lookup is not readiness.
  const isConversationsVisibilityReady =
    isAdmin ||
    (!!user?.id &&
      !campaignsSubscriptionsIsLoading &&
      !hasCampaignSubscriptionsError);

  // Undefined for an admin, which lifts the restriction entirely.
  const conversationsVisibility = useMemo(() => {
    if (isAdmin || !user?.id) {
      return undefined;
    }

    return {
      userId: user.id,
      subscribedCampaignIds: campaignSubscriptions
        .map((subscription) => subscription.campaign?.id)
        .filter((id): id is string => !!id)
    };
  }, [isAdmin, user?.id, campaignSubscriptions]);

  const { total: conversationsCount, isLoading: isConversationsCountLoading } =
    useCrossCampaignConversationsCount({
      organizationId: organization?.id,
      visibility: conversationsVisibility,
      skip: !isConversationsTabEnabled || !isConversationsVisibilityReady
    });

  const tabs = useMemo(() => {
    return [
      {
        label: labelByValue[TabsValue.RECRUITMENT],
        value: TabsValue.RECRUITMENT,
        count: campaignsCount
      },
      ...(isConversationsTabEnabled
        ? [
            {
              label: labelByValue[TabsValue.CONVERSATIONS],
              value: TabsValue.CONVERSATIONS,
              count: conversationsCount,
              isCountLoading:
                isConversationsCountLoading || !isConversationsVisibilityReady
            }
          ]
        : [])
    ];
  }, [
    isConversationsTabEnabled,
    conversationsCount,
    isConversationsCountLoading,
    isConversationsVisibilityReady,
    campaignsCount
  ]);

  useEffect(() => {
    if (isAdmin) {
      setIsChecked(true);
    }
  }, [isAdmin]);

  const openConversationSettingsDrawer = () => {
    openDrawerV2({
      width: '500px',
      title: 'Conversation settings',
      children: <ConversationSettingsDrawer />
    });
  };

  return (
    <Box sx={container}>
      <Typography variant="h3" sx={pageTitle}>
        Campaigns
      </Typography>

      <Box>
        <TabsV2
          containerSx={tabBar}
          tabs={tabs}
          onChange={(value) => {
            setSelectedTab(value as TabsValue);
          }}
          value={tab}
          endItems={[
            <Button
              key="export-data-button"
              variant="tertiary"
              onClick={exportData}
              disabled={isExporting || hasExported}
            >
              {getExportButtonLabel()}
            </Button>,
            ...(isAdmin
              ? [
                  <GlobalContextButton key="global-context-button" />,
                  <Button
                    key="conversation-settings-button"
                    variant="tertiary"
                    onClick={openConversationSettingsDrawer}
                  >
                    Conversation settings
                  </Button>
                ]
              : []),
            <StartCampaignFeatureFlag
              key="start-campaign-button"
              buttonSx={newCampaignButton}
            />
          ]}
        />
        {tab !== TabsValue.CONVERSATIONS && (
          <Box sx={checkBoxesFilterContainer}>
            {isAdmin && (
              <Checkbox
                label="Only show my campaigns"
                onChange={(e) => {
                  setIsChecked(!e.target.checked);
                }}
                checked={!isChecked}
              />
            )}
          </Box>
        )}
      </Box>

      {tab === TabsValue.CONVERSATIONS && organization?.id && (
        <CrossCampaignConversationsTab
          organizationId={organization.id}
          visibility={conversationsVisibility}
          // Hold the query until the restriction is built: an unknown user, or
          // an unloaded subscription list, would briefly query unrestricted or
          // under-restricted. A failed lookup is not readiness either.
          isVisibilityReady={isConversationsVisibilityReady}
          visibilityError={!isAdmin && hasCampaignSubscriptionsError}
          onRetryVisibility={campaignSubscriptionsRefetch}
          onExportFilterChange={setConversationsExportFilter}
        />
      )}
      {tab !== TabsValue.CONVERSATIONS && list && (
        <TableV2
          data={list}
          columns={columns}
          onFilterSelect={onFilterSelect}
          onRemoveFilters={(filterId) =>
            setFilters((prevFilters) =>
              prevFilters.filter((filter) => filter.filterId !== filterId)
            )
          }
          isLoading={isLoading}
          pagination
          onChangeRowsPerPage={() => {
            track(TrackingEventName.CampaignsListRowsNumberUpdate);
          }}
          handleNext={() => {
            track(TrackingEventName.CampaignsListNextPage);
          }}
          sortField={sortKey}
          onSort={(key) => {
            if (!key.id) return;
            setSortKey(key.id as string);
          }}
          filters={filters}
          onRemoveAllFilters={() => setFilters([])}
        />
      )}
    </Box>
  );
};
